import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import uniq from 'lodash-es/uniq';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Chips } from '@/components/atoms/Chip/Chip';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogs from '@/hooks/useDialogs';
import { updateContext } from '@/modules/tryIt/redux/actions';
import { selectContext, selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { selectAllTags } from '@/redux/dialogs/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { MAX_CONVERSATION_TAGS } from '@/util/constants';
import { newTagSchema } from '@/util/validation/schemas/dialog-schemas';

import { ContextSectionWrapper } from '../ContextSectionWrapper/ContextSectionWrapper';

interface ActionTag {
  tag: string;
}

interface Props {
  tags: string[];
  canEdit: boolean;
}

const TagsSection = ({ tags = [], canEdit }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);
  const allTags = selectAllTags(dialogs);
  const isReplay = useSelector(selectIsReplay);

  const suggestedTags = allTags.filter((t) => !tags.includes(t));

  const storeContext = useSelector(selectContext);

  const {
    formState: { errors },
    setValue,
    control,
  } = useForm<ActionTag>({
    mode: 'onChange',
    defaultValues: { tag: '' },
    resolver: yupResolver(newTagSchema) as Resolver<ActionTag>,
  });

  const autocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      suggestedTags?.map((tag) => ({
        category: t('dialog.tags.auto_complete_group_by'),
        value: tag,
        label: tag,
      })) ?? [],
    [suggestedTags, t]
  );

  const handleOnTagDelete = useCallback(
    (t: string) => {
      const newTags = tags.filter((tag: string) => tag !== t);
      const newContext = storeContext[storeContext.length - 1] ?? {};

      dispatch(updateContext([...storeContext.slice(0, -1), { ...newContext, tags: newTags }]));
    },
    [dispatch, storeContext, tags]
  );

  const handleChange = useCallback(
    (value: string) => {
      if (value) {
        const newTags = uniq([...tags, value]);
        const newContext = storeContext[storeContext.length - 1] ?? {};

        dispatch(updateContext([...storeContext.slice(0, -1), { ...newContext, tags: newTags }]));
        setValue('tag', '');
      }
    },
    [dispatch, setValue, storeContext, tags]
  );

  const handleCreateNew = useCallback(
    (value: string) => {
      if (newTagSchema.isValidSync({ tag: value })) {
        handleChange(value);
      }
    },
    [handleChange]
  );

  const resetTags = useCallback(() => {
    const newContext = storeContext[storeContext.length - 1] ?? {};

    dispatch(updateContext([...storeContext.slice(0, -1), { ...newContext, tags: [] }]));
  }, [dispatch, storeContext]);

  if ((!canEdit || isReplay) && tags.length === 0) {
    return null;
  }

  const hideResetIcon = !canEdit || isReplay || (tags.length === 0 && canEdit);

  return (
    <ContextSectionWrapper title={t('common.tags')} onClick={resetTags} hideIcon={hideResetIcon}>
      {canEdit && !isReplay && (
        <FormAutocomplete
          name="tag"
          control={control}
          options={autocompleteOptions}
          placeholder={t('rules.type_tag')}
          error={!!errors.tag?.message}
          errorMessage={errors.tag?.message}
          disabled={tags?.length >= MAX_CONVERSATION_TAGS}
          onChange={handleChange}
          onCreateNew={handleCreateNew}
        />
      )}

      <Chips
        className="mt-2"
        chips={tags}
        onDelete={(tag) => {
          handleOnTagDelete(tag);
        }}
        hideDeleteIcon={!canEdit || isReplay}
      />
    </ContextSectionWrapper>
  );
};

export default TagsSection;
